import { useState, useEffect } from "react";
import { api } from "@/lib/api";
import { Loader2, CheckCircle2 } from "lucide-react";
import { formatDetail } from "@/lib/format";

export default function ResetPassword() {
  const [cfg, setCfg] = useState({});
  const [token, setToken] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/signin-config").then((r) => setCfg(r.data)).catch(() => {});
    const params = new URLSearchParams(window.location.search);
    setToken(params.get("token") || "");
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 8) return setError("Password must be at least 8 characters.");
    if (password !== confirm) return setError("Passwords do not match.");
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      setDone(true);
    } catch (err) {
      setError(formatDetail(err.response?.data?.detail, "Could not reset password."));
    } finally {
      setBusy(false);
    }
  };

  const inputCls = "w-full bg-warm-cream border border-ink/20 rounded-xl px-4 py-3 min-h-[50px] text-[16px] focus:bg-white mt-1";

  return (
    <div className="min-h-screen flex items-center justify-center bg-warm-cream p-8">
      <div className="w-full max-w-md bg-cream border border-ink/15 rounded-2xl p-8 shadow-[0_20px_50px_-25px_rgba(20,20,15,0.4)] animate-rise">
        <div className="flex justify-center mb-5"><img src="/logo-full.png" alt="Rosemoor" className="h-14" /></div>
        {done ? (
          <div className="text-center py-4" data-testid="reset-success">
            <CheckCircle2 className="w-12 h-12 text-olive mx-auto mb-3" />
            <h2 className="font-zilla text-2xl font-semibold mb-2">Password updated</h2>
            <p className="text-ink/70 text-[16px] mb-6">You can now sign in with your new password.</p>
            <a href="/" data-testid="reset-goto-signin" className="inline-block bg-maroon text-cream font-semibold rounded-xl px-6 py-3 min-h-[48px] text-[16px]">Go to sign in</a>
          </div>
        ) : !token ? (
          <div className="text-center py-6" data-testid="reset-invalid">
            <h2 className="font-zilla text-2xl font-semibold mb-2">Invalid reset link</h2>
            <p className="text-ink/70 text-[16px]">This link is missing its token. Request a new one from the sign-in screen.</p>
            <a href="/" className="inline-block mt-5 text-dark-olive font-semibold hover:underline">Back to sign in</a>
          </div>
        ) : (
          <>
            <h2 className="font-zilla text-3xl font-semibold mb-1">{cfg.reset_headline || "Choose a new password"}</h2>
            <p className="text-ink/70 mb-6 text-[16px]">{cfg.reset_subtext || "Enter a new password for your account."}</p>
            {error && <div data-testid="reset-error" className="bg-maroon/10 border border-maroon/30 rounded-lg px-4 py-3 mb-4 text-[15px]">{error}</div>}
            <form onSubmit={submit} className="space-y-3">
              <div>
                <label className="text-[14px] font-semibold">New password</label>
                <input data-testid="reset-password-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={8} className={inputCls} />
              </div>
              <div>
                <label className="text-[14px] font-semibold">Confirm new password</label>
                <input data-testid="reset-confirm-input" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required className={inputCls} />
              </div>
              <button data-testid="reset-submit-button" type="submit" disabled={busy}
                className="w-full flex items-center justify-center gap-2 bg-maroon text-cream font-semibold rounded-xl px-5 py-4 min-h-[52px] text-lg disabled:opacity-50">
                {busy && <Loader2 className="w-5 h-5 animate-spin" />} {cfg.reset_submit || "Update password"}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
